import React from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  Label,
} from "recharts";
import { DetailedModelMetrics, EdaData, EdaLabelStats } from "../../types";
import "./PredictionResultDashboard.css"; // Import CSS for styling

interface PredictionResultDashboardProps {
  predictionResult: Record<string, number> | null;
  modelData: DetailedModelMetrics | null;
  edaData: EdaData | null;
}

// Helper to get EDA stats for a specific target label
const getLabelStats = (
  edaData: EdaData | null,
  labelName: string,
): EdaLabelStats | undefined => {
  return edaData?.eda_results?.labels_stats?.[labelName];
};

export const PredictionResultDashboard: React.FC<
  PredictionResultDashboardProps
> = ({ predictionResult, modelData, edaData }) => {
  if (!predictionResult || !modelData || !modelData.model_info || !edaData) {
    // This case should ideally be handled by the conditional rendering in App.tsx
    return (
      <p>
        Erforderliche Daten für das Dashboard fehlen (Vorhersage, Modelldetails
        oder EDA-Daten).
      </p>
    );
  }

  const targetNames = Object.keys(predictionResult!);

  return (
    <div className="prediction-dashboard-container">
      <h2>
        Vorhersage Ergebnisse im Kontext für Modell:{" "}
        {modelData.model_info.name || "Unbekanntes Modell"}
      </h2>
      <div className="prediction-dashboard-grid">
        {targetNames.map((targetName) => {
          const predictedValue = predictionResult![targetName];
          const stats = getLabelStats(edaData, targetName);

          // Validate stats and get numbers or fallbacks
          let edaMin: number | null = null;
          let edaMax: number | null = null;
          let edaMean: number | undefined = undefined;

          if (
            stats?.min !== null &&
            stats?.min !== undefined &&
            !isNaN(stats.min)
          ) {
            edaMin = stats.min;
          }
          if (
            stats?.max !== null &&
            stats?.max !== undefined &&
            !isNaN(stats.max)
          ) {
            edaMax = stats.max;
          }
          if (
            stats?.mean !== null &&
            stats?.mean !== undefined &&
            !isNaN(stats.mean)
          ) {
            edaMean = stats.mean;
          }

          // Determine domain, ensure it's always numbers
          const fallbackRange = Math.abs(predictedValue * 0.5) || 1;
          let domainMinCalc = edaMin ?? predictedValue - fallbackRange;
          let domainMaxCalc = edaMax ?? predictedValue + fallbackRange;

          // Ensure domain has a range
          if (domainMinCalc === domainMaxCalc) {
            domainMinCalc -= 1;
            domainMaxCalc += 1;
          }
          const range = domainMaxCalc - domainMinCalc; // Now guaranteed to be numbers
          const padding = Math.abs(range * 0.1) || 1;

          const finalDomain: [number, number] = [
            domainMinCalc - padding,
            domainMaxCalc + padding,
          ];

          const chartData = [{ name: targetName, prediction: predictedValue }];

          return (
            <div key={targetName} className="prediction-target-card card">
              {/* Use validated numbers for display */}
              <h4>{targetName}</h4>
              <p className="predicted-value">
                Vorhersage: <strong>{predictedValue.toFixed(4)}</strong>
              </p>
              <div className="eda-stats">
                {edaMin !== null && edaMax !== null ? (
                  <span>
                    EDA Min: {edaMin.toFixed(2)} | EDA Max: {edaMax.toFixed(2)}
                  </span>
                ) : (
                  <span>(Keine EDA Min/Max Info)</span>
                )}
                {edaMean !== undefined && (
                  <span style={{ marginLeft: "1rem" }}>
                    EDA Mean: {edaMean.toFixed(2)}
                  </span>
                )}
              </div>

              <div className="prediction-chart-wrapper">
                <ResponsiveContainer width="100%" height={100}>
                  <BarChart
                    data={chartData}
                    layout="vertical"
                    margin={{ top: 5, right: 40, left: 10, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      type="number"
                      domain={finalDomain}
                      tickFormatter={(tick) => tick.toFixed(1)}
                      height={20}
                      tick={{ fontSize: 10 }}
                    />
                    <YAxis type="category" dataKey="name" hide />
                    <Tooltip
                      formatter={(value: number) => [
                        value.toFixed(4),
                        "Vorhersage",
                      ]}
                    />

                    {/* Render ReferenceLine only if edaMean is a number */}
                    {typeof edaMean === "number" && (
                      <ReferenceLine
                        x={edaMean}
                        stroke="var(--color-orange)"
                        strokeDasharray="4 4"
                      >
                        <Label
                          value={`EDA Mean (${edaMean.toFixed(2)})`}
                          position="insideTopRight"
                          fill="var(--color-orange)"
                          fontSize={10}
                        />
                      </ReferenceLine>
                    )}

                    <Bar
                      dataKey="prediction"
                      fill="var(--accent-color)"
                      barSize={30}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
